import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Search } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { usePageMeta } from '@/hooks/usePageMeta';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import CxAssessmentForm from '@/components/cx/CxAssessmentForm';
import { BASE_URL } from './cxData';

const CxAssessmentPage = () => {
  const { t } = useTranslation();
  const cx = t.cx;
  const a = cx.assessment;

  usePageMeta({ title: cx.meta.assessmentTitle, description: cx.meta.assessmentDescription, canonical: `${BASE_URL}/cx/assessment` });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${BASE_URL}/cx/assessment#page`,
        url: `${BASE_URL}/cx/assessment`,
        name: cx.meta.assessmentTitle,
        description: cx.meta.assessmentDescription,
        isPartOf: { '@id': `${BASE_URL}/#website` },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: cx.breadcrumbs.home, item: `${BASE_URL}/` },
          { '@type': 'ListItem', position: 2, name: cx.breadcrumbs.cx, item: `${BASE_URL}/cx` },
          { '@type': 'ListItem', position: 3, name: cx.breadcrumbs.assessment, item: `${BASE_URL}/cx/assessment` },
        ],
      },
    ],
  };

  return (
    <>
      <CustomCursor />
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <main style={{ paddingTop: 100 }}>
        <div className="max-w-6xl mx-auto px-6 mb-6">
          <nav className="flex items-center gap-2 text-sm" style={{ color: 'var(--slate-light)', fontFamily: 'var(--f-mono)' }}>
            <Link to="/" className="hover:text-[var(--amber)] transition-colors">{cx.breadcrumbs.home}</Link>
            <span>/</span>
            <Link to="/cx" className="hover:text-[var(--amber)] transition-colors">{cx.breadcrumbs.cx}</Link>
            <span>/</span>
            <span style={{ color: 'var(--ink)' }}>{cx.breadcrumbs.assessment}</span>
          </nav>
        </div>

        <section className="max-w-6xl mx-auto px-6 pb-24">
          <div className="grid lg:grid-cols-[1fr_1.15fr] gap-10 items-start">
            {/* Left: what and why */}
            <div>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-5" style={{ fontFamily: 'var(--f-display)', color: 'var(--ink)' }}>{a.title}</h1>
              <p className="text-lg leading-relaxed mb-10" style={{ color: 'var(--slate)' }}>{a.subtitle}</p>

              {[{ block: a.review, Icon: Search }, { block: a.receive, Icon: CheckCircle2 }].map(({ block, Icon }, i) => (
                <div key={i} className="p-7 rounded-2xl mb-5" style={{ background: i === 0 ? 'var(--off-white)' : 'var(--blue-light)', border: '1px solid var(--line)' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'var(--blue)', color: 'var(--white)' }}><Icon size={17} /></div>
                    <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--f-display)', color: 'var(--ink)' }}>{block.title}</h2>
                  </div>
                  <ul className="space-y-2">
                    {block.items.map((item, j) => (
                      <li key={j} className="flex gap-3 leading-relaxed" style={{ color: 'var(--slate)' }}>
                        <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--amber)' }} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              <p className="text-sm leading-relaxed" style={{ color: 'var(--slate-light)' }}>{cx.cta.secondary}</p>
            </div>

            {/* Right: the form (.contact-form-wrap supplies the card styling) */}
            <CxAssessmentForm />
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default CxAssessmentPage;
